import React, { useEffect, useMemo, useState } from 'react';
import { Button, Flex, Item, Picker, Text } from '@adobe/react-spectrum';
import { CardStatRow } from './CardStatRow';
import './cardStats.css';

/**
 * Shared, filterable list surface for every card list shown across the
 * commander details tabs (Cards, Combos, Synergy, Manabase, Archetypes).
 *
 * Accepts a `renderCard` prop so callers with row components that differ
 * from the default CardStatRow (e.g. SynergyCardRow with ability text,
 * SynergyAnchorRow with score chips) can plug their renderer in while
 * still reusing the filter + pagination behavior.
 *
 * Backend exposes `types` as a lowercase free-text type line (e.g.
 * "creature", "legendary creature — dragon") and `categories` as a
 * string[] of role-ish tags. Mana value isn't on per-card rows today.
 */

const TYPE_ORDER = [
    'creature',
    'planeswalker',
    'battle',
    'artifact',
    'enchantment',
    'instant',
    'sorcery',
    'land',
];

const TYPE_LABELS = {
    creature: 'Creature',
    planeswalker: 'Planeswalker',
    battle: 'Battle',
    artifact: 'Artifact',
    enchantment: 'Enchantment',
    instant: 'Instant',
    sorcery: 'Sorcery',
    land: 'Land',
};

const KNOWN_TYPES = new Set(TYPE_ORDER);

const defaultTypeSet = (card) => {
    const raw = (card?.types || '').toLowerCase();
    if (!raw) return new Set();
    const beforeDash = raw.split('—')[0];
    const tokens = beforeDash.split(/\s+/).filter(Boolean);
    return new Set(tokens.filter((t) => KNOWN_TYPES.has(t)));
};

const defaultGetName = (item) => item?.name || '';

/**
 * Combo adapter — a combo row is a bundle of cards. Name search must
 * match if any card in the combo matches, and the type filter is the
 * union of types across all cards in the combo.
 */
const comboGetName = (combo) =>
    (combo?.cards || []).map((c) => c?.name || '').join(' | ');

const comboGetTypeSet = (combo) => {
    const union = new Set();
    (combo?.cards || []).forEach((card) => {
        defaultTypeSet(card).forEach((t) => union.add(t));
    });
    return union;
};

export const CARD_STAT_LIST_ADAPTERS = {
    card: { getName: defaultGetName, getTypeSet: defaultTypeSet },
    combo: { getName: comboGetName, getTypeSet: comboGetTypeSet },
};

const INITIAL_VISIBLE_DEFAULT = 30;
const EXPAND_STEP_DEFAULT = 50;

export function CardStatList({
    cards,
    deckCount,
    onSelectCard,
    initialVisible = INITIAL_VISIBLE_DEFAULT,
    expandStep = EXPAND_STEP_DEFAULT,
    renderCard,
    emptyMessage = 'No cards match these filters.',
    emptyMessageUnfiltered,
    hideFilters = false,
    compactFilters = false,
    resetKey,
    getName = defaultGetName,
    getTypeSet = defaultTypeSet,
    searchPlaceholder = 'Filter by name…',
    searchAriaLabel = 'Filter cards by name',
    loadMoreLabel = (remaining) => `Show more (${remaining} remaining)`,
    sortOptions,
    initialSortKey,
    sortLabel = 'Sort',
}) {
    const [nameQuery, setNameQuery] = useState('');
    const [activeTypes, setActiveTypes] = useState(() => new Set());
    const [visibleCount, setVisibleCount] = useState(initialVisible);
    const hasSortOptions = Array.isArray(sortOptions) && sortOptions.length > 0;
    const defaultSortKey = initialSortKey || (hasSortOptions ? sortOptions[0].key : null);
    const defaultDirForKey = (key) => {
        const opt = hasSortOptions ? sortOptions.find((o) => o.key === key) : null;
        return opt?.defaultDir || (opt?.isText ? 'asc' : 'desc');
    };
    const [sortKey, setSortKey] = useState(defaultSortKey);
    const [sortDir, setSortDir] = useState(() => defaultDirForKey(defaultSortKey));

    const [filtersOpen, setFiltersOpen] = useState(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true;
        return window.matchMedia('(min-width: 768px)').matches;
    });

    // Reset state when the underlying data swaps (bin/bracket/filter
    // changes). Callers pass a stable `resetKey` to trigger this.
    useEffect(() => {
        setNameQuery('');
        setActiveTypes(new Set());
        setVisibleCount(initialVisible);
        if (hasSortOptions) {
            const nextKey = initialSortKey || sortOptions[0].key;
            setSortKey(nextKey);
            setSortDir(defaultDirForKey(nextKey));
        }
    }, [resetKey, initialVisible]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSortKeyChange = (nextKey) => {
        setSortKey(nextKey);
        setSortDir(defaultDirForKey(nextKey));
    };

    const safeCards = Array.isArray(cards) ? cards : [];

    const availableTypes = useMemo(() => {
        const counts = new Map();
        safeCards.forEach((item) => {
            getTypeSet(item).forEach((t) => {
                counts.set(t, (counts.get(t) || 0) + 1);
            });
        });
        return TYPE_ORDER.filter((t) => counts.has(t)).map((t) => ({
            type: t,
            count: counts.get(t),
        }));
    }, [safeCards, getTypeSet]);

    const activeSort =
        hasSortOptions && sortKey
            ? sortOptions.find((o) => o.key === sortKey) || sortOptions[0]
            : null;

    const buildComparator = (option, direction) => {
        if (!option) return null;
        if (typeof option.compare === 'function') {
            return direction === 'desc'
                ? (a, b) => option.compare(b, a)
                : option.compare;
        }
        const getValue = option.getValue || (() => 0);
        const dirMul = direction === 'desc' ? -1 : 1;
        if (option.isText) {
            return (a, b) => {
                const av = String(getValue(a) || '');
                const bv = String(getValue(b) || '');
                return av.localeCompare(bv) * dirMul;
            };
        }
        return (a, b) => {
            const av = Number(getValue(a) || 0);
            const bv = Number(getValue(b) || 0);
            if (av === bv) return 0;
            return (av - bv) * dirMul;
        };
    };

    const filteredCards = useMemo(() => {
        const q = nameQuery.trim().toLowerCase();
        const base =
            !q && activeTypes.size === 0
                ? safeCards.slice()
                : safeCards.filter((item) => {
                    if (q) {
                        const name = getName(item).toLowerCase();
                        if (!name.includes(q)) return false;
                    }
                    if (activeTypes.size > 0) {
                        const types = getTypeSet(item);
                        let matches = false;
                        for (const t of activeTypes) {
                            if (types.has(t)) {
                                matches = true;
                                break;
                            }
                        }
                        if (!matches) return false;
                    }
                    return true;
                });
        const comparator = buildComparator(activeSort, sortDir);
        if (comparator) base.sort(comparator);
        return base;
    }, [safeCards, nameQuery, activeTypes, getName, getTypeSet, activeSort, sortDir]);

    // Pagination window resets when filters or sort change so the user
    // always sees the top of the filtered list.
    useEffect(() => {
        setVisibleCount(initialVisible);
    }, [nameQuery, activeTypes, sortKey, initialVisible]);

    const visibleCards = filteredCards.slice(0, visibleCount);
    const hasMore = visibleCount < filteredCards.length;

    const toggleType = (type) => {
        setActiveTypes((prev) => {
            const next = new Set(prev);
            if (next.has(type)) next.delete(type);
            else next.add(type);
            return next;
        });
    };

    const clearAll = () => {
        setNameQuery('');
        setActiveTypes(new Set());
    };

    const filtersActive = nameQuery.trim().length > 0 || activeTypes.size > 0;

    const listIsEmpty = safeCards.length === 0;
    const filteredIsEmpty = !listIsEmpty && filteredCards.length === 0;

    return (
        <Flex direction="column" rowGap={8}>
            {!hideFilters && safeCards.length > 0 && (
                <div className={`cardStatListFiltersWrap${filtersOpen ? ' is-open' : ''}`}>
                    <button
                        type="button"
                        className="cardStatListFiltersToggle"
                        aria-expanded={filtersOpen}
                        aria-controls="cardStatListFiltersBody"
                        onClick={() => setFiltersOpen((v) => !v)}
                    >
                        <span className="cardStatListFiltersChevron" aria-hidden="true">
                            {filtersOpen ? '▾' : '▸'}
                        </span>
                        <span className="cardStatListFiltersTitle">Filters</span>
                        {filtersActive && (
                            <span className="cardStatListFiltersBadge">
                                {filteredCards.length} of {safeCards.length}
                            </span>
                        )}
                    </button>
                    {filtersOpen && (
                <div
                    id="cardStatListFiltersBody"
                    className={`cardStatListFilters ${
                        compactFilters ? 'is-compact' : ''
                    }`}
                >
                    <input
                        type="search"
                        className="cardStatListSearch"
                        placeholder={searchPlaceholder}
                        value={nameQuery}
                        onChange={(evt) => setNameQuery(evt.target.value)}
                        aria-label={searchAriaLabel}
                    />
                    {hasSortOptions && (
                        <div className="cardStatListSortWrap">
                            <Picker
                                aria-label={sortLabel}
                                selectedKey={sortKey}
                                onSelectionChange={(key) => handleSortKeyChange(key)}
                                items={sortOptions}
                                UNSAFE_className="cardStatListSort"
                            >
                                {(option) => (
                                    <Item key={option.key}>{option.label}</Item>
                                )}
                            </Picker>
                            <button
                                type="button"
                                className={`cardStatListSortDir cardStatListSortDir--${sortDir}`}
                                onClick={() =>
                                    setSortDir((prev) => (prev === 'desc' ? 'asc' : 'desc'))
                                }
                                aria-label={`Toggle sort direction (currently ${sortDir === 'desc' ? 'descending' : 'ascending'})`}
                                title={`Sort ${sortDir === 'desc' ? 'descending — click for ascending' : 'ascending — click for descending'}`}
                            >
                                {sortDir === 'desc' ? '▼' : '▲'}
                            </button>
                        </div>
                    )}
                    {availableTypes.length > 1 && (
                        <div className="cardStatListChipRow">
                            {availableTypes.map(({ type, count }) => {
                                const isActive = activeTypes.has(type);
                                return (
                                    <button
                                        key={type}
                                        type="button"
                                        className={`cardStatListChip ${
                                            isActive ? 'is-active' : ''
                                        }`}
                                        onClick={() => toggleType(type)}
                                        title={`${count} ${TYPE_LABELS[type]} card${
                                            count === 1 ? '' : 's'
                                        }`}
                                    >
                                        <img
                                            src={`/resources/typeIcons/${type}_symbol.svg`}
                                            alt=""
                                            aria-hidden="true"
                                            className="cardStatListChipIcon"
                                        />
                                        <span className="cardStatListChipLabel">
                                            {TYPE_LABELS[type]}
                                        </span>
                                        <span className="cardStatListChipCount">{count}</span>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                    {filtersActive && (
                        <button
                            type="button"
                            className="cardStatListClear"
                            onClick={clearAll}
                        >
                            Clear filters
                        </button>
                    )}
                    {filtersActive && (
                        <span className="cardStatListResultCount">
                            {`${filteredCards.length} of ${safeCards.length}`}
                        </span>
                    )}
                </div>
                    )}
                </div>
            )}
            {listIsEmpty ? (
                <Text UNSAFE_className="commanderCardStatsEmpty">
                    {emptyMessageUnfiltered || emptyMessage}
                </Text>
            ) : filteredIsEmpty ? (
                <Text UNSAFE_className="commanderCardStatsEmpty">{emptyMessage}</Text>
            ) : (
                <>
                    <Flex direction="column" rowGap={6}>
                        {visibleCards.map((card) =>
                            renderCard ? (
                                renderCard(card)
                            ) : (
                                <CardStatRow
                                    key={card.cardId}
                                    card={card}
                                    deckCount={deckCount}
                                    onSelectCard={onSelectCard}
                                />
                            )
                        )}
                    </Flex>
                    {hasMore && (
                        <Flex direction="row" justifyContent="center" marginTop={10}>
                            <Button
                                variant="secondary"
                                onPress={() => setVisibleCount((prev) => prev + expandStep)}
                            >
                                {loadMoreLabel(filteredCards.length - visibleCount)}
                            </Button>
                        </Flex>
                    )}
                </>
            )}
        </Flex>
    );
}
